import React, { useEffect, useState, useMemo } from "react";
import { t } from "i18next";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { Divider, Tooltip } from "@mui/material";

import { ButtonComp, Icon, ProgressiveImage } from "components";
import { ToolTipIcon } from "icons";
import { getIsMobileWindow, getVisibleInPersonTour, getVisibleLiveVideoTour, getVisibleScheduleTour, getVisibleSelfGuidedTour } from "slices";
import { selectLabelItems, setShouldFetchSlots } from "slices/param-slice/index";
import { selectAPIKey, selectPropertyId } from "slices/property-slice/index";
import { setBedRooms, setBudgets, setIsUnitTourScreen, setUnitFilters, setUnits, setAllFloorPlans } from "slices/units-slice/index";
import { addNewMessage, removeAndAddMessages } from "slices/messages-slice/index";
import { createItemRecord, getBedroomTypes, getPriceBrackets, getUnits, sendHayleyMessage } from "utils/methods/index";
import { ERROR_MSG, FORM_4_SCHEDULE_TOUR_OPTIONS, INITIAL_REPLIES } from "utils/constants/index";
import { setReplies } from "slices/replies-slice/index";
import { budgetFormat, findNearestAvailability, formatUnitData, getPriceRange, isValidDateRange } from "components/UnitsView/utils";
import { getAppSSID } from "slices/window-slice/index";
import { resetScheduleTourState } from "slices/schedule-tour-slice/index";
import FeeTransparency from "components/FeeTransparency";

const ModalView = ({
  data: incomingData,
  setFocusedUnitIdx,
  dataArr,
  startScheduleTour,
  go2ContactUs,
  isShowAll = false,
  isFloorPlan = false,
  unitCase,
  feesData = [],
  hidePricing,
  tmlpEnabled,
}) => {
  const labelItems = useSelector(selectLabelItems);
  const SSID = useSelector(getAppSSID);

  const [direction, setDirection] = useState("forward");
  const [isAnimating, setIsAnimating] = useState(false);

  const visibleScheduleTour = useSelector(getVisibleScheduleTour);
  const visibleInPersonTour = useSelector(getVisibleInPersonTour);
  const visibleLiveVideoTour = useSelector(getVisibleLiveVideoTour);
  const visibleSelfGuidedTour = useSelector(getVisibleSelfGuidedTour);
  const isMobile = useSelector(getIsMobileWindow);

  const dispatch = useDispatch();
  const propertyId = useSelector(selectPropertyId);

  const apiKey = useSelector(selectAPIKey);

  const [data, setData] = useState(incomingData);
  const [expandedUnits, setExpanded] = useState(false);

  const toggleExpand = () => {
    if (data.units?.length > 0 && unitCase == "2") {
      setExpanded((p) => !p);
    } else {
      handleUnitClick();
    }
  };

  useEffect(() => {
    let dup = { ...incomingData };

    if (isFloorPlan) {
      dup = {
        ...incomingData,
        area: incomingData.fp_area_min,
        unit_description: incomingData.fp_description,
        unit_type: incomingData?.fp_type,
        unit_floor_plan_image: incomingData.fp_image,
        unit_name: incomingData.fp_name,
        unit_rent_min: incomingData.fp_rent_min,
        unit_url: incomingData.fp_url,
        availability_date: incomingData.units?.[0]?.made_ready_date,
        is_available: incomingData.units?.[0]?.is_available,
        unit_bedroom: incomingData.units?.[0]?.unit_bedroom,
        unit_bathroom: incomingData.units?.[0]?.unit_bathroom,
        tour_link: incomingData?.fp_availability_url,
      };
    }

    setData(dup);
  }, [incomingData]);

  const nearestDate = useMemo(() => {
    const date = findNearestAvailability(incomingData?.units);

    return date;
  }, [incomingData.units]);

  const handleUnitClick = async () => {
    let filter = {
      bedrooms: data?.unit_type,
      allFloorPlans: { id: data?.id, name: data?.fp_name },
    };

    const apartment_context = localStorage.getItem(`${propertyId}-context`);

    if (!!apartment_context) {
      const appContext = JSON.parse(apartment_context);

      if (appContext?.length > 0) {
        if (appContext[0]?.desiredMoveInDate) {
          filter["moveIns"] = appContext[0]?.desiredMoveInDate;
        }

        if (appContext[0]?.priceRange) {
          filter["budgets"] = appContext[0]?.priceRange;
        }
      }
    } else {
      if (labelItems?.desired_move_in) {
        filter["moveIns"] = labelItems?.desired_move_in;
      }

      if (labelItems?.price) {
        filter["budgets"] = budgetFormat(labelItems?.price);
      }
    }

    try {
      dispatch(addNewMessage([{ message: "loadingAllUnit", speaker: "bot", createdAt: new Date().toISOString() }]));

      const budgets = getPriceRange(filter.budgets) || null;

      const length = budgets?.length || 0;

      const desiredMoveIn = filter["moveIns"];

      const min = length === 0 ? null : length > 1 ? budgets[0] : 0;
      const max = length === 0 ? null : length > 1 ? budgets[1] : budgets[0];

      let convertedDate = desiredMoveIn;
      let isRange = false;

      if (moment(desiredMoveIn, "MMM DD, YYYY", true).isValid()) {
        convertedDate = moment(desiredMoveIn, "MMM DD, YYYY").format("YYYY-MM-DD");
      }

      const alreadyDotted = desiredMoveIn?.startsWith("..");

      if (isValidDateRange(desiredMoveIn)) {
        isRange = true;
        convertedDate = desiredMoveIn.split(",")?.join("..");
      }

      const queryParams = `?property_id=${propertyId}&api_key=${encodeURIComponent(apiKey)}${filter.bedrooms ? `&bedroom_type=${filter.bedrooms}` : ""}${
        !desiredMoveIn || desiredMoveIn === "any"
          ? ""
          : `&desired_move_in=${(filter?.isAsap || !isRange) && !alreadyDotted ? "..." + convertedDate || desiredMoveIn : convertedDate || desiredMoveIn}`
      }${min ? `&min_price=${min}` : ""}${max ? `&max_price=${max}` : ""}`;

      const units = await getUnits(queryParams);
      const floor_plans = units?.data?.floor_plans?.map((i) => ({ name: i.fp_name, id: i.id }));
      const isSelectedFloorPresent = floor_plans.some((floor) => floor.id === filter?.allFloorPlans?.id && floor.name === filter?.allFloorPlans?.name);

      if (!isSelectedFloorPresent) {
        filter["allFloorPlans"] = {
          id: null,
          name: "All",
        };
      }

      dispatch(setUnitFilters(filter));

      const bedTypes = await getBedroomTypes(`?api_key=${encodeURIComponent(apiKey)}&property_id=${propertyId}`);

      const priceRange = await getPriceBrackets(
        `?property_id=${propertyId}&api_key=${encodeURIComponent(apiKey)}&bedroom_type=${data?.unit_type || bedTypes?.data[0]?.value}`
      );

      dispatch(setIsUnitTourScreen(true));
      dispatch(setUnits(units?.data || null));
      dispatch(setBedRooms(bedTypes?.data || null));
      dispatch(setBudgets(priceRange?.data || null));
      dispatch(setAllFloorPlans(floor_plans || null));
      dispatch(removeAndAddMessages([]));
    } catch (e) {
      dispatch(removeAndAddMessages([{ message: ERROR_MSG, speaker: "bot", subject: "", createdAt: new Date().toISOString() }]));
      dispatch(setReplies(INITIAL_REPLIES));
    }
  };

  const record = (clickValue) => {
    createItemRecord(propertyId, SSID, clickValue);
  };

  const handleSchedule = () => {
    record("schedule a tour");
    // Clear cached tour data and force refetch
    dispatch(resetScheduleTourState());
    dispatch(setShouldFetchSlots(true));
    startScheduleTour(data);
  };

  const handleContactMe = () => {
    sendHayleyMessage({
      text: "Contact Me",
      queryType: "question",
      ownerType: "prospect",
      kgNodeValue: "Contact Me",
    });

    dispatch(
      addNewMessage([
        {
          message: "Contact Me",
          speaker: "user",
          optionsType: "Init",
          key: "contact_us",
          createdAt: new Date().toISOString(),
        },
      ])
    );

    record("contact us");

    go2ContactUs();
  };

  const handleContactMe2 = () => {
    dispatch(setIsUnitTourScreen(false));

    sendHayleyMessage({
      text: "Contact Me",
      queryType: "question",
      ownerType: "prospect",
      kgNodeValue: "Contact Me",
    });

    dispatch(
      addNewMessage([
        {
          message: "Contact Me",
          speaker: "user",
          optionsType: "Init",
          key: "contact_us",
          createdAt: new Date().toISOString(),
        },
        {
          message: t("fewMoreDetailGet"),
          speaker: "bot",
          createdAt: new Date().toISOString(),
        },
      ])
    );

    sendHayleyMessage({
      text: t("fewMoreDetailGet"),
      queryType: "answer",
      ownerType: "agent",
      kgNodeValue: t("fewMoreDetailGet"),
    });

    let rep = {
      checkStorage: true,
      disableResetBox: false,
      options: FORM_4_SCHEDULE_TOUR_OPTIONS,
      optionsType: "Form4ContactUs",
      title: "Enter First Name and Last Name",
      renderType: "multiInputView",
    };

    dispatch(setReplies(rep));
    record("contact us");
  };

  const formattedData = useMemo(() => formatUnitData(data), [data]);

  const handleNext = () => {
    setDirection("forward");
    setIsAnimating(true);
    setFocusedUnitIdx((prev) => (prev < dataArr.length - 1 ? prev + 1 : 0));

    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  const handlePrev = () => {
    setDirection("backward");
    setIsAnimating(true);
    setFocusedUnitIdx((prev) => (prev > 0 ? prev - 1 : dataArr.length - 1));

    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  return (
    <div
      data-testid="modalView"
      className={`hayley-relative hayley-mb-2 hayley-mt-3 hayley-h-fit hayley-max-w-[calc(100vw-25px)] hayley-select-none hayley-rounded-lg hayley-border hayley-border-solid hayley-border-[#EEE] sm:hayley-w-full sm:hayley-max-w-full ${
        isShowAll ? "hayley-overflow-visible" : "hayley-overflow-hidden"
      }

     ${isShowAll && !isMobile ? "hayley-mr-2 !hayley-w-[466px]" : ""}
    `}
    >
      {isShowAll ? null : (
        <>
          {dataArr.length > 1 && (
            <div
              className={`hayley-absolute hayley-left-3 ${
                unitCase == "2" ? "hayley-top-[126px]" : "hayley-top-[106px]"
              } hayley-z-20 hayley-flex hayley-h-10 hayley-w-10 -hayley-translate-y-1/2 hayley-cursor-pointer hayley-items-center hayley-justify-center hayley-rounded-full hayley-bg-white hayley-shadow-lg`}
              onClick={handlePrev}
              data-testid="leftArrow"
            >
              <Icon iconType="Chevron" className="hayley-h-5 hayley-w-5 hayley-rotate-180 hayley-text-secondary" />
            </div>
          )}
          {dataArr.length > 1 && (
            <div
              className={`hayley-absolute hayley-right-3 ${
                unitCase == "2" ? "hayley-top-[126px]" : "hayley-top-[106px]"
              } hayley-z-20 hayley-flex hayley-h-10 hayley-w-10 -hayley-translate-y-1/2 hayley-cursor-pointer hayley-items-center hayley-justify-center hayley-rounded-full hayley-bg-white hayley-shadow-lg`}
              onClick={handleNext}
              data-testid="rightArrow"
            >
              <Icon iconType="Chevron" className="hayley-h-5 hayley-w-5 hayley-text-secondary" />
            </div>
          )}
        </>
      )}
      <div
        className={`hayley-relative hayley-overflow-hidden ${
          isAnimating ? (direction === "forward" ? "hayley-animate-slideInFromRight" : "hayley-animate-slideInFromLeft") : ""
        }`}
      >
        <div data-testid="unit-header" className="hayley-relative hayley-space-y-2 hayley-text-center">
          {data?.unit_floor_plan_image?.[0]?.image_url ? (
            <ProgressiveImage
              classes={{
                root: `hayley-mx-auto ${unitCase == "2" ? "hayley-h-[220px]" : "hayley-h-[180px]"} hayley-w-auto hayley-overflow-hidden`,
                img: "hayley-h-full hayley-w-full hayley-object-contain hayley-object-center hayley-border-solid hayley-border-[#EEE] hayley-border-0  hayley-border-b-[1px] hayley-shadow-md hayley-mt-4",
              }}
              src={data.unit_floor_plan_image[0].image_url}
              alt=""
              selectedId={""}
              testId={`unitView_${data.unit_name}_floorPlanImage`}
            />
          ) : (
            <>
              <div
                data-testid={`unitView_${data.unit_name}_floorPlanIcon`}
                className={`hayley-flex ${
                  unitCase == "2" ? "hayley-h-[220px]" : "hayley-h-[180px]"
                } hayley-flex-col hayley-items-center  hayley-justify-center`}
              >
                <Icon iconType={"PlaceholderImage"} />
                <p className="hayley-text-[12px] hayley-text-[#999999]">Image coming soon...</p>
              </div>
              <Divider />
            </>
          )}
        </div>
        <FeeTransparency
          totalPrice={data?.total_price}
          baseRent={data?.unit_rent_min || data?.fp_rent_min}
          leaseTerm={data?.external_lease_term || data?.units?.[0]?.external_lease_term}
          fees={feesData}
          unitBedrooms={formattedData?.unitType}
          unitArea={formattedData?.area}
          availabilityDate={nearestDate || formattedData?.availabilityDate}
          hidePricing={hidePricing}
          tmlpEnabled={tmlpEnabled}
        />

        <div className="hayley-flex hayley-flex-wrap hayley-px-4 hayley-pt-3">
          {visibleScheduleTour && data?.is_available && (visibleInPersonTour || visibleLiveVideoTour || visibleSelfGuidedTour) ? (
            <ButtonComp
              testId="unitView_scheduleTour"
              label={t("scheduleTour")}
              className="hayley-mb-2 hayley-mr-2"
              onClick={handleSchedule}
              disabled={false}
              filled={true}
            />
          ) : !data.show_link && ["1", "3", "4"].includes(unitCase) ? (
            <ButtonComp
              testId="unitView_contactMe"
              label={t("contactMe")}
              className="hayley-mb-2 hayley-mr-2"
              onClick={handleContactMe}
              disabled={false}
              filled={true}
            />
          ) : null}

          {data?.tour_link && data?.is_available && (
            <ButtonComp
              icon={"OpenLinkIcon"}
              testId="unitView_applyNow_tourLink"
              label={t("applyNow")}
              onClick={() => window.open(data?.tour_link, "_blank")}
              disabled={false}
              isLeftIcon={false}
              className="hayley-mb-2 hayley-mr-2 !hayley-px-5 !hayley-py-[10px]"
            />
          )}
          {isFloorPlan ? null : data?.unit_availability_url ? (
            <ButtonComp
              icon={"OpenLinkIcon"}
              label={t("applyNow")}
              testId="unitView_applyNow_unitAvailabilityUrl"
              onClick={() => window.open(data?.unit_availability_url, "_blank")}
              disabled={false}
              isLeftIcon={false}
              className="hayley-mb-2 hayley-mr-2 !hayley-px-5 !hayley-py-[10px]"
            />
          ) : (
            <ButtonComp testId="unitView_contactMe" label={t("contactMe")} className="hayley-mb-2 hayley-mr-2" onClick={handleContactMe2} disabled={false} />
          )}
        </div>

        {/* Available units section */}
        {isFloorPlan ? (
          <div className="hayley-overflow-hidden hayley-px-4 hayley-pb-4 hayley-pt-1">
            <div data-testid="unitView_allAvailableUnits" className="hayley-max-w-[calc(100vw-30px)] sm:hayley-min-w-[420px] sm:hayley-max-w-full">
              {unitCase == "2" || (data.show_link && ["1", "3", "4"].includes(unitCase)) ? (
                <div
                  data-testid={`unitView_${data.fp_name}_toggle_button`}
                  className="hayley-flex hayley-w-fit hayley-cursor-pointer hayley-items-center"
                  onClick={toggleExpand}
                >
                  <div
                    data-testid="unit_available"
                    className={`hayley-font-semibold ${unitCase == "2" ? "hayley-text-[#7b7b7b]" : "hayley-text-[#4F6FAE] hover:hayley-text-[#4dd3f8]"}`}
                  >{`${data.units_count} available unit${data.units_count > 1 ? "s" : ""}`}</div>
                  <span className="hayley-ml-2 hayley-mt-1">
                    {unitCase == "2" && (
                      <>
                        {expandedUnits ? (
                          <svg className="hayley-h-5 hayley-w-5" fill="none" stroke="#7b7b7b" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        ) : (
                          <svg className="hayley-h-5 hayley-w-5 " fill="none" stroke="#7b7b7b" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                          </svg>
                        )}
                      </>
                    )}
                  </span>
                </div>
              ) : data?.units?.length < 1 && data?.units_count < 1 && isShowAll && data?.is_available ? (
                <ButtonComp testId="unitView_contactMe" label={t("contactMe")} className="hayley-mb-2 hayley-mr-2" onClick={handleContactMe2} filled={true} />
              ) : null}

              <div data-testid="unit-table" className={`${expandedUnits ? "hayley-block" : "hayley-hidden"} mt-2 hayley-max-w-[85vw] hayley-overflow-auto`}>
                <div
                  style={{ borderBottom: "1px solid #eee" }}
                  className="hayley-flex hayley-justify-between hayley-border-b-2 hayley-border-[#d7d7d7] hayley-py-2 hayley-text-sm hayley-text-[#7B7B7B]"
                  data-testid="unitView_availableUnitsTableHeader"
                >
                  <div className="hayley-w-[80px] hayley-font-semibold sm:hayley-w-[70px]">Unit</div>
                  <div className="hayley-w-[70px] hayley-font-semibold">Area</div>
                  <div className="hayley-w-[110px] hayley-font-semibold">Availability</div>
                  {tmlpEnabled && (
                    <div className="hayley-flex hayley-w-[70px] hayley-items-center hayley-justify-center hayley-gap-1 hayley-font-semibold">
                      <span>TMLP</span>
                      <Tooltip
                        title="Total monthly Leasing Price"
                        arrow
                        placement="top"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: "#333333",
                              color: "#ffffff",
                              fontSize: "14px",
                              fontFamily: "Open Sans",
                              fontWeight: 400,
                              padding: "6px 10px",
                              lineHeight: "20px",
                              "& .MuiTooltip-arrow": {
                                color: "#333333",
                              },
                            },
                          },
                        }}
                      >
                        <div className="hayley-flex hayley-cursor-pointer hayley-items-center">
                          <ToolTipIcon classes="hayley-h-4 hayley-w-4" fill="#666666" />
                        </div>
                      </Tooltip>
                    </div>
                  )}
                  {!tmlpEnabled && !hidePricing && (
                    <div className="hayley-w-[70px] hayley-text-center hayley-font-semibold">
                      <span>Base Rent</span>
                    </div>
                  )}
                  <div className="hayley-w-[80px] hayley-cursor-pointer  hayley-text-end hayley-text-sm hayley-font-semibold hayley-text-[#4F6FAE] hover:hayley-text-[#4dd3f8]" />
                </div>

                {data?.units?.slice(0, 3)?.map((unit, index) => {
                  const formattedTableData = formatUnitData(unit);

                  return (
                    <div
                      id="unit-table-row"
                      key={index}
                      style={{ borderBottom: "1px solid #eee" }}
                      className="hayley-flex hayley-justify-between hayley-break-words hayley-border-b-2 hayley-border-[#d7d7d7] hayley-py-2 hayley-text-sm hayley-text-[#7B7B7B]"
                      data-testid={`unitView_${data.fp_name}_row`}
                    >
                      <div data-testid="unitView_unit_name" className=" hayley-w-[80px] sm:hayley-w-[70px] ">
                        {formattedTableData.name}
                      </div>
                      <div data-testid="unitView_area" className="hayley-w-[70px]">
                        {formattedTableData.areaFt}
                      </div>
                      <div data-testid="unitView_availability_date" className="hayley-w-[112px]">
                        {formattedTableData.availabilityDate}
                      </div>
                      {tmlpEnabled && (
                        <div data-testid="unitView_unit_rent_min" className="hayley-w-[70px] hayley-text-center hayley-font-semibold hayley-text-[#333333]">
                          {formattedTableData.totalPrice}
                        </div>
                      )}
                      {!tmlpEnabled && !hidePricing && (
                        <div data-testid="unitView_base_rent" className="hayley-w-[70px] hayley-text-center hayley-font-semibold hayley-text-[#333333]">
                          {formattedTableData.unitRentMin}
                        </div>
                      )}
                      {unit?.unit_availability_url ? (
                        <div
                          id="unitView_applyNow"
                          data-testid={`unitView_${formattedTableData.name}_applyNow`}
                          className="hayley-min-w-[60px] hayley-cursor-pointer hayley-whitespace-nowrap hayley-text-end hayley-text-sm hayley-font-semibold hayley-text-[#4F6FAE] hover:hayley-text-[#4dd3f8]"
                          onClick={() => window.open(unit?.unit_availability_url, "_blank")}
                        >
                          {t("applyNow")}
                        </div>
                      ) : (
                        <div
                          id="unitView_contactMe"
                          data-testid={`unitView_${formattedTableData.name}_contactMe`}
                          className="hayley-min-w-[60px] hayley-cursor-pointer hayley-whitespace-nowrap hayley-text-end hayley-text-sm hayley-font-semibold hayley-text-[#4F6FAE] hover:hayley-text-[#4dd3f8]"
                          onClick={handleContactMe}
                        >
                          {t("contactMe")}
                        </div>
                      )}
                    </div>
                  );
                })}
                {data?.units?.length > 3 ? (
                  <div
                    className="hayley-flex hayley-cursor-pointer hayley-justify-between hayley-border-b-2 hayley-border-[#d7d7d7] hayley-py-2 hayley-text-sm hayley-font-semibold hayley-text-[#4F6FAE] hover:hayley-text-[#4dd3f8]"
                    onClick={handleUnitClick}
                    data-testid="unitView_seeAllAvailableUnits"
                  >
                    {t("seeAll")}
                  </div>
                ) : data?.units?.length < 1 && data?.units_count < 1 ? (
                  <ButtonComp testId="unitView_contactMe" label={t("contactMe")} className="hayley-mb-2 hayley-mr-2" onClick={handleContactMe} filled={true} />
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ModalView;
